import React, { useEffect, useState, useMemo, useRef } from 'react'
import { Carousel } from 'antd'
import { useStores } from '@/store'
import { getQuery } from '../../service/http'
import { useTranslation } from 'react-i18next'
import { isMobile, handleDeepLink } from '@/service/utils'
import './eventBlock.less'

import {
  createHomepageTrackingEvent,
  createOnceExposureObserver,
  trackHomepageEvent,
} from './tracking'

const EventBlock = ({ currentEvent }) => {
  const { project, website_url, dc_homepage_color, accent_color } = currentEvent
  const { t } = useTranslation()
  const { region, source = '' } = getQuery()
  const { HomeStore, memberStore } = useStores()
  const mobile = useMemo(() => isMobile(), [])
  const [logo, setLogo] = useState(null)
  const [banner, setBanner] = useState([])
  const [shortCuts, setShortCuts] = useState([])
  const [contentBlock, setContentBlock] = useState([])
  const contentCarouselRef = useRef(null)
  const shortCutsCarouselRef = useRef(null)
  useEffect(() => {
    HomeStore.getEventBlockLogo(project).then(setLogo)
    HomeStore.getBanner(project, mobile ? 'dc_homepage_banner_mobile' : 'dc_homepage_banner_web').then(setBanner)
    HomeStore.getShortCuts(region, project, mobile).then(setShortCuts)
    HomeStore.getContentBlock(region, project, mobile).then(res => {
      const trackedItems = res.map((item, index) => ({
        ...item,
        trackingPosition: index + 1,
      }))
      if (trackedItems.length < 3 && trackedItems.length > 0) {
        setContentBlock([trackedItems])
        return
      }
      const arr = []
      const num = Math.floor(trackedItems.length / 3)
      for (let i = 0; i < num; i++) {
        arr.push(trackedItems.slice(i * 3, (i + 1) * 3))
      }
      if (trackedItems.length % 3 !== 0 && trackedItems.length > 3) {
        arr.push(trackedItems.slice(trackedItems.length - 3, trackedItems.length))
      }
      setContentBlock(arr)
    })
  }, [])
  const swiper = useMemo(() => {
    if (shortCuts?.length) {
      let tempCuts = [...shortCuts]
      let tempSwiper = []
      const step = 10
      for (let index = 0; index < tempCuts.length; index += step) {
        tempSwiper.push(tempCuts.slice(index, index + step))
      }
      return tempSwiper
    } else {
      return []
    }
  }, [shortCuts])
  const shortCutPositions = useMemo(
    () => new Map(shortCuts.map((item, index) => [item.id, index + 1])),
    [shortCuts],
  )
  const exposureTracker = useMemo(
    () =>
      createOnceExposureObserver((event) => {
        if (event) trackHomepageEvent(event.eventName, event.params)
      }),
    [],
  )
  useEffect(
    () => () => {
      exposureTracker.disconnect()
    },
    [exposureTracker],
  )
  const getTrackingContext = () => ({
    project,
    city: region || '',
    userId: memberStore.member?.id,
    source,
  })
  const getTrackingEvent = (type, item, position) =>
    createHomepageTrackingEvent(
      type,
      getTrackingContext(),
      item,
      position,
    )
  const trackEvent = (type, item, position) => {
    const event = getTrackingEvent(type, item, position)
    if (event) trackHomepageEvent(event.eventName, event.params)
  }
  const observeExposure = (element, key, type, item, position) => {
    exposureTracker.observe(element, key, () =>
      getTrackingEvent(type, item, position),
    )
  }
  const splitIntoChunks = (arr, chunkSize = 5) => {
    const chunks = []
    for (let i = 0; i < arr.length; i += chunkSize) {
      chunks.push(arr.slice(i, i + chunkSize))
    }
    return chunks
  }
  return (
    <div className='event-block'>
      <div className='event-block-context' style={{ background: dc_homepage_color || accent_color }}>
        {banner.img_url && (
          <div className='event-block-context-banner'>
            <img
              ref={element => observeExposure(element, 'event-banner', 'banner_exposure', banner, 1)}
              onClick={() => {
                trackEvent('banner_click', banner, 1)
                window.open(website_url, '_blank')
              }}
              src={banner.img_url}
              alt=''
            />
          </div>
        )}
        {!!shortCuts.length && (
          <div className='event-block-context-shortCuts'>
            <div className='shortcuts-mobile'>
              <Carousel adaptiveHeight={true} infinite={false}>
                {swiper.map((cuts, index) => (
                  <div key={index}>
                    <div className='shortcuts-item' style={{ justifyContent: shortCuts.length < 5 ? 'center' : 'left' }}>
                      {cuts.map(item => (
                        <a
                          key={item.id}
                          ref={element => observeExposure(
                            element,
                            `shortcut:${item.id}`,
                            'shortcut_exposure',
                            item,
                            shortCutPositions.get(item.id),
                          )}
                          href={item?.links?.web || item?.links?.ios}
                          className='shortcut'
                          onClick={(event) => {
                            event.preventDefault()
                            trackEvent('shortcut_click', item, shortCutPositions.get(item.id))
                            handleDeepLink(item?.links?.web || item?.links?.ios)
                          }}
                        >
                          <img src={item.icon_url} alt='' />
                          <div className='title'>{item.title}</div>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </Carousel>
            </div>
            <div className='shortcuts-pc'>
              <Carousel autoplay={false} ref={shortCutsCarouselRef} adaptiveHeight={true} dots={false}>
                {swiper.map((cuts, index) => (
                  <div key={index}>
                    <div>
                      {splitIntoChunks(cuts).map(lineItem => (
                        <div key={lineItem[0]?.id} className='shortcuts-item' style={{ justifyContent: shortCuts.length < 5 ? 'center' : 'left' }}>
                          {lineItem.map(item => (
                            <a
                              key={item.id}
                              ref={element => observeExposure(
                                element,
                                `shortcut:${item.id}`,
                                'shortcut_exposure',
                                item,
                                shortCutPositions.get(item.id),
                              )}
                              href={item?.links?.web || item?.links?.ios}
                              className='shortcut'
                              onClick={(event) => {
                                event.preventDefault()
                                trackEvent('shortcut_click', item, shortCutPositions.get(item.id))
                                handleDeepLink(item?.links?.web || item?.links?.ios)
                              }}
                            >
                              <img src={item.icon_url + '-thumb'} alt='' />
                              <div className='title'>{item.title}</div>
                            </a>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </Carousel>
              {swiper.length > 1 && (
                <div className='tigger'>
                  <div className='prev' onClick={() => shortCutsCarouselRef.current.prev()}>
                    <i className='icon icon-arrow-left'></i>
                  </div>
                  <div className='next' onClick={() => shortCutsCarouselRef.current.next()}>
                    <i className='icon icon-arrow-right'></i>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {!!contentBlock.length && (
          <div className='event-block-context-content'>
            <div className='event-block-banners'>
              <Carousel autoplay ref={contentCarouselRef}>
                {contentBlock.map(arr => (
                  <div key={arr.map(item => item.id).join('-')} style={{ display: 'flex', justifyContent: 'left' }}>
                    {arr.map(item => (
                      <div
                        key={item.id}
                        ref={element => observeExposure(
                          element,
                          `content-block:${item.id}`,
                          'content_block_exposure',
                          item,
                          item.trackingPosition,
                        )}
                        className='banner-item'
                        onClick={() => {
                          trackEvent('content_block_click', item, item.trackingPosition)
                          handleDeepLink(item.link)
                        }}
                      >
                        <img src={item.image_url} alt='' />
                      </div>
                    ))}
                  </div>
                ))}
              </Carousel>
              <div className='tigger'>
                <div onClick={() => contentCarouselRef.current.prev()}></div>
                <div onClick={() => contentCarouselRef.current.next()}></div>
              </div>
            </div>
          </div>
        )}
        <div className='event-block-context-logo'>
          <img src={logo?.img_url} alt='' onClick={() => window.open(website_url, '_blank')} />
        </div>
      </div>
      <div className='event-block-text'>{t('WEB.Also on DiningCity')}</div>
    </div>
  )
}

export default EventBlock
